import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useChat } from '@/hooks/useChat'
import { Search, Send, Plus, ArrowLeftCircle, Paperclip, Users } from 'lucide-react'
import { useRef, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { format } from 'date-fns'
import { Badge } from '@/components/ui/badge'
import { guardTeacherStudentParent } from '@/lib/auth/routeGuards'
import { ExtendedUser, Message, ExtendedConversation } from '@/types/chat'
import { useTranslation } from 'react-i18next'
import ChatFileUpload from '@/components/chat/ChatFileUpload'
import ChatFileMessage from '@/components/chat/ChatFileMessage'
import { useUserStore } from '@/store/userStore'
import GroupChatView from '@/components/chat/GroupChatView'
import CreateGroupDialog from '@/components/chat/CreateGroupDialog'

// Define API URL with fallback options
const API_URL = import.meta.env.VITE_API_URL
const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL
// Extended User interface to include the properties used in this component

// Extended Conversation interface to include isGroupChat

// Utility functions for chat
const chatUtils = {
  getInitials: (user: ExtendedUser | any) => {
    if (!user) return 'U'
    const firstName = user.firstname || user.firstName || ''
    const lastName = user.lastname || user.lastName || ''
    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
    }
    return user.username?.charAt(0)?.toUpperCase() || 'U'
  },
  getAvatarUrl: (user: ExtendedUser | any) => {
    if (!user) return ''

    // First try to use the user's actual avatar if available
    if (user.avatar) {
      // Use CdnImage component's approach to handle CDN URLs
      if (
        user.avatar.includes(cdnBaseUrl) ||
        user.avatar.includes('46.101.199.181')
      ) {
        return `${API_URL}/health/proxy/image?url=${encodeURIComponent(user.avatar)}`
      }
      return user.avatar
    }

    // Fall back to placeholder if no avatar
    const firstName = user.firstname || user.firstName || ''
    const lastName = user.lastname || user.lastName || ''
    const fullName = `${firstName} ${lastName}`.trim()

    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      user.displayName ||
      fullName ||
      user.username ||
      'User'
    )}&size=128&background=random`
  },
}

export const Route = createFileRoute('/chat/')({
  beforeLoad: guardTeacherStudentParent,
  validateSearch: (search: Record<string, unknown>): { conversationId?: string } => {
    return {
      conversationId: search.conversationId as string | undefined,
    }
  },
  component: ChatPage,
})

function ChatPage() {
  return <ChatContent />
}

// Add default export for lazy loading
export default ChatPage

function ChatContent() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const {
    conversations,
    selectedConversation,
    selectedConversationId,
    message,
    setMessage,
    isLoading,
    searchTerm,
    setSearchTerm,
    searchResults,
    isSearching,
    isSearchLoading,
    messagesEndRef,
    handleSendMessage,
    handleSendFileMessage,
    startNewConversation,
    handleSelectConversation,
    getOtherParticipant,
    toggleSearch,
    conversationFilter,
    setConversationFilter,
  } = useChat()

  // Add state for file upload integration
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const fileUploadRef = useRef<any>(null)

  // Add state for create group dialog
  const [isCreateGroupOpen, setIsCreateGroupOpen] = useState(false)

  // Function to go back to the appropriate dashboard based on user role
  function goBack() {
    const userRole = localStorage.getItem('role')
    let redirectPath = '/'

    switch (userRole) {
      case 'Admin':
        redirectPath = '/admin'
        break
      case 'SuperAdmin':
        redirectPath = '/super_admin'
        break
      case 'Teacher':
        redirectPath = '/teacher'
        break
      case 'Parent':
        redirectPath = '/parent'
        break
      case 'Student':
        redirectPath = '/student'
        break
      default:
        redirectPath = '/'
    }

    navigate({ to: redirectPath })
  }

  // For backward compatibility, get currentUser from localStorage
  const [currentUser, setCurrentUser] = useState<ExtendedUser | null>(null)

  // Create a ref for the messages container
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)

  // Create a map of message refs for intersection observer
  const messageRefs = useRef<Map<string, HTMLDivElement>>(new Map())

  // Set up intersection observer to mark messages as read when they become visible
  useEffect(() => {
    if (!selectedConversation || !selectedConversationId) return

    // Create an intersection observer
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageId = entry.target.getAttribute('data-message-id')
            if (messageId) {
              // Find the message in the selected conversation
              const message = selectedConversation.messages.find(
                (msg: Message) => msg.id === messageId
              )

              // If the message is from another user and not read, mark it as read
              if (
                message &&
                message.senderId !== currentUser?.id &&
                !message.isRead
              ) {
                // The message is now visible, so it's considered read
                // The actual marking as read is handled by the handleSelectConversation function
                // This is just to trigger a UI update if needed
            // console.logMessage visible and marked as read:', messageId)
              }
            }
          }
        })
      },
      { threshold: 0.5 } // 50% of the message must be visible
    )

    // Observe all message elements
    messageRefs.current.forEach((ref) => {
      if (ref) {
        observer.observe(ref)
      }
    })

    return () => {
      // Clean up observer
      observer.disconnect()
    }
  }, [selectedConversation, selectedConversationId, currentUser])

  // Function to store message refs
  const setMessageRef = (id: string, element: HTMLDivElement | null) => {
    if (element) {
      messageRefs.current.set(id, element)
    } else {
      messageRefs.current.delete(id)
    }
  }

  // Initialize currentUser from userStore (fetched from API)
  const userStoreUser = useUserStore((state) => state.user)

  useEffect(() => {
    if (userStoreUser && userStoreUser.id && userStoreUser.role && userStoreUser.firstname && userStoreUser.lastname) {
      setCurrentUser({
        id: userStoreUser.id,
        firstname: userStoreUser.firstname,
        lastname: userStoreUser.lastname,
        role: userStoreUser.role as 'Teacher' | 'Student' | 'Parent',
      })
    }
  }, [userStoreUser])

  // Add a state to track loading timeout
  const [loadingTimeout, setLoadingTimeout] = useState(false)

  // Set a timeout for loading
  useEffect(() => {
    let timeoutId: NodeJS.Timeout

    if (isLoading) {
      timeoutId = setTimeout(() => {
        setLoadingTimeout(true)
      }, 10000) // 10 seconds timeout
    } else {
      setLoadingTimeout(false)
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [isLoading])

  // Scroll to bottom when component mounts or when conversation changes
  useEffect(() => {
    if (selectedConversation && messagesEndRef.current) {
      // Scroll to bottom immediately
      messagesEndRef.current.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      })

      // Also scroll after a short delay to ensure all content is loaded
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({
            behavior: 'auto',
            block: 'end',
          })
        }
      }, 100)
    }
  }, [selectedConversation?.id])

  // Fix the handleSelectConversation call
  const onSelectConversation = (conversation: ExtendedConversation) => {
    if (conversation && conversation.id) {
      handleSelectConversation(conversation.id)
      // Update URL to reflect selected conversation
      navigate({
        to: '.',
        search: (old) => ({ ...old, conversationId: conversation.id }),
        replace: true,
      })
    }
  }

  // Get search params to check for conversationId
  const search = Route.useSearch()

  // Add a useEffect to handle initial selection from URL or default to most recent
  useEffect(() => {
    // If a conversationId is provided in the URL, select it
    if (search.conversationId && search.conversationId !== selectedConversationId) {
      // We need to verify if this conversation exists in the loaded list or fetch it
      const conversationExists = conversations.some(c => c.id === search.conversationId)

      if (conversationExists) {
        handleSelectConversation(search.conversationId)
      } else {
        // If it's not in the list yet (maybe loading or pagination), we might want to try selecting it anyway
        // The useChat hook's handleSelectConversation handles fetching individual conversation details
        handleSelectConversation(search.conversationId)
      }
    }
    // If no URL param and no selection, default to the most recent conversation
    else if (conversations.length > 0 && !selectedConversationId && !search.conversationId) {
      // Find the most recent conversation with unread messages
      const unreadConversation = conversations.find((conv) =>
        conv.messages.some(
          (msg) => !msg.isRead && msg.senderId !== currentUser?.id
        )
      )

      // If there's a conversation with unread messages, select it
      if (unreadConversation) {
        handleSelectConversation(unreadConversation.id)
      }
      // Otherwise, select the most recent conversation for all users
      else if (conversations[0]) {
        handleSelectConversation(conversations[0].id)
      }
    }
  }, [
    conversations,
    selectedConversationId,
    currentUser,
    handleSelectConversation,
    search.conversationId
  ])

  // Modified send message handler that checks for files
  const handleSendMessageWithFile = async (e?: React.FormEvent) => {
    e?.preventDefault()

    // If there's a file staged, send it with the message
    if (selectedFile && fileUploadRef.current) {
      try {
        await fileUploadRef.current.handleSend()
        // Clear the selected file state after successful send
        setSelectedFile(null)
        return
      } catch (error) {
        // console.error('Error sending file message:', error)
        return
      }
    }

    // If no file, send regular text message
    if (message.trim()) {
      handleSendMessage(e)
    }
  }

  // Handle file selection from ChatFileUpload component
  const handleFileSelected = (file: File | null) => {
    setSelectedFile(file)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg font-medium text-gray-700">
          {t('routes.chat.loading.title')}
        </p>

        {loadingTimeout && (
          <div className="mt-8 max-w-md text-center p-4 bg-white rounded-lg shadow">
            <p className="text-amber-600 font-medium mb-2">
              Taking longer than expected
            </p>
            <p className="text-gray-600 mb-4">
              There might be an issue connecting to the server. You can try:
            </p>
            <ul className="text-left text-gray-600 mb-4 space-y-2">
              <li>• Checking your internet connection</li>
              <li>• Refreshing the page</li>
              <li>• Logging out and logging back in</li>
            </ul>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Refresh Page
            </button>
          </div>
        )}
      </div>
    )
  }

  if (!currentUser) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <p className="text-lg font-medium text-gray-700">
          Loading user data...
        </p>

        {loadingTimeout && (
          <div className="mt-8 max-w-md text-center p-4 bg-white rounded-lg shadow">
            <p className="text-amber-600 font-medium mb-2">
              Unable to load user data
            </p>
            <p className="text-gray-600 mb-4">This could be due to:</p>
            <ul className="text-left text-gray-600 mb-4 space-y-2">
              <li>• Your session may have expired</li>
              <li>• There might be an issue with the server</li>
              <li>• You might need to log in again</li>
            </ul>
            <button
              onClick={() => (window.location.href = '/login')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
            >
              Go to Login
            </button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col h-[calc(100vh-60px)] bg-gray-100">
      {/* Back button */}
      <div className="bg-white p-2 border-b border-gray-200">
        <button
          onClick={goBack}
          className="flex items-center gap-2 p-2 rounded-lg text-gray-700 hover:bg-gray-200 hover:text-gray-900 text-left"
        >
          <ArrowLeftCircle className="h-5 w-5 text-gray-700" />
          <span className="text-sm font-medium">
            {t('routes.chat.backToDashboard')}
          </span>
        </button>
      </div>

      {/* Chat container */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - reduced width */}
        <div className="w-1/5 bg-white border-r border-gray-200 flex flex-col">
          {/* User info - more compact */}
          <div className="p-2 border-b border-gray-200">
            <div className="flex items-center space-x-2">
              {/* <Avatar className="h-8 w-8">
              <AvatarImage src={chatUtils.getAvatarUrl(currentUser)} />
              <AvatarFallback>
                {chatUtils.getInitials(currentUser)}
              </AvatarFallback>
            </Avatar> */}
              <div>
                <p className="font-medium text-sm">
                  {currentUser.firstname} {currentUser.lastname}
                </p>
                <p className="text-xs text-gray-500">({currentUser.role})</p>
              </div>
            </div>
          </div>

          {/* Search or conversation list toggle - more compact */}
          <div className="p-2 border-b border-gray-200">
            <div className="flex space-x-1">
              <Button
                variant={isSearching ? 'default' : 'outline'}
                className="flex-1 text-xs h-8"
                onClick={toggleSearch}
              >
                {isSearching
                  ? t('routes.chat.sidebar.toggleButtons.backToChats')
                  : t('routes.chat.sidebar.toggleButtons.newChat')}
              </Button>
              {(currentUser?.role === 'Teacher' ||
                currentUser?.role === 'Parent') && (
                  <>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={toggleSearch}
                      title={
                        currentUser.role === 'Teacher'
                          ? t('routes.chat.sidebar.newChatButton.teacher')
                          : t('routes.chat.sidebar.newChatButton.parent')
                      }
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => setIsCreateGroupOpen(true)}
                      title="Create Group Chat"
                    >
                      <Users className="h-3 w-3" />
                    </Button>
                  </>
                )}
            </div>
          </div>

          {/* Search UI or Conversation list */}
          {isSearching ? (
            <div className="flex-1 overflow-auto">
              <div className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={
                      currentUser.role === 'Teacher'
                        ? t('routes.chat.sidebar.search.teacher')
                        : t('routes.chat.sidebar.search.parent')
                    }
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Search results */}
              <div className="p-4">
                {isSearchLoading ? (
                  <p className="text-center text-gray-500">
                    {t('routes.chat.sidebar.searchResults.searching')}
                  </p>
                ) : searchResults.length > 0 ? (
                  <div className="space-y-2">
                    {searchResults.map((user: ExtendedUser) => (
                      <div
                        key={user.id}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => startNewConversation(user.id)}
                      >
                        <Avatar>
                          <AvatarImage src={chatUtils.getAvatarUrl(user)} />
                          <AvatarFallback>
                            {chatUtils.getInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">
                            {user.firstname} {user.lastname}
                          </p>
                          <p className="text-sm text-gray-500 truncate">
                            {user.role}
                          </p>
                          {user.displayName &&
                            user.displayName !==
                            `${user.firstname} ${user.lastname}` && (
                              <p className="text-xs text-gray-400 truncate">
                                ({user.displayName})
                              </p>
                            )}
                          {user.username && (
                            <p className="text-xs text-gray-400 truncate">
                              @{user.username}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : searchTerm ? (
                  <p className="text-center text-gray-500">
                    {t('routes.chat.sidebar.searchResults.noResults')}
                  </p>
                ) : (
                  <p className="text-center text-gray-500">
                    {currentUser.role === 'Teacher'
                      ? t(
                        'routes.chat.sidebar.searchResults.emptyPrompt.teacher'
                      )
                      : t(
                        'routes.chat.sidebar.searchResults.emptyPrompt.parent'
                      )}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              {/* Filter conversations */}
              <div className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={t('routes.chat.sidebar.search.filter')}
                    className="pl-10"
                    value={conversationFilter}
                    onChange={(e) => setConversationFilter(e.target.value)}
                  />
                </div>
              </div>

              {/* Conversations list */}
              <div className="space-y-1 p-2">
                {conversations.length > 0 ? (
                  conversations.map((conversation) => {
                    // Debug logging to understand the data structure
                // console.logConversation data:', conversation)
                // console.logCurrent user ID:', currentUser.id)
                // console.logParticipants:', conversation.participants)

                    // Try to get the other user more reliably
                    let otherUser = getOtherParticipant(conversation)
                // console.loggetOtherParticipant result:', otherUser)

                    // If getOtherParticipant doesn't work, try to find manually
                    if (!otherUser && conversation.participants) {
                      otherUser = conversation.participants.find(
                        (participant) => participant.id !== currentUser.id
                      )
                  // console.logManual search result:', otherUser)
                    }

                    // Try additional fallback methods
                    if (!otherUser) {
                      // Check if participants is an array and has data
                      if (Array.isArray(conversation.participants)) {
                    // console.logParticipants array:', conversation.participants)
                        // Try with string comparison in case of type mismatch
                        otherUser = conversation.participants.find(
                          (participant) => String(participant.id) !== String(currentUser.id)
                        )
                    // console.logString comparison result:', otherUser)
                      }

                      // Try to get user info from message senders
                      if (!otherUser && conversation.messages && conversation.messages.length > 0) {
                        // Look for a message from someone other than current user
                        const messageFromOther = conversation.messages.find(
                          (msg) => msg.senderId !== currentUser.id && msg.sender
                        )
                        if (messageFromOther && messageFromOther.sender) {
                          otherUser = messageFromOther.sender
                      // console.logFound user from message sender:', otherUser)
                        }
                      }

                      // If still no luck, try to extract from conversation name
                      if (!otherUser && conversation.name && conversation.name.includes('-')) {
                        const parts = conversation.name.split('-').map(p => p.trim())
                        const currentUserName = `${currentUser.firstname} ${currentUser.lastname}`
                        const otherUserName = parts[0] === currentUserName ? parts[1] : parts[0]

                        // Create a minimal user object from the name
                        if (otherUserName && otherUserName !== currentUserName) {
                          const nameParts = otherUserName.split(' ')
                          otherUser = {
                            id: 'unknown',
                            firstname: nameParts[0] || '',
                            lastname: nameParts.slice(1).join(' ') || '',
                            role: 'Student' // Default role
                          }
                      // console.logCreated user from name:', otherUser)
                        }
                      }
                    }

                    // Determine if this is the selected conversation
                    const isSelected =
                      selectedConversation?.id === conversation.id

                    // Use the conversation's unreadCount from server, not calculated from messages
                    // This ensures consistency with server state
                    const hasUnread = isSelected ? false : (conversation.unreadCount || 0) > 0
                    const unreadCount = isSelected ? 0 : (conversation.unreadCount || 0)

                    // Debug logging for student notification issue
                    if (currentUser?.role === 'Student' && hasUnread) {
                  // console.log('Student unread debug:', {
                  //       conversationId: conversation.id,
                  //       selectedId: selectedConversation?.id,
                  //       isSelected,
                  //       unreadCount: conversation.messages.filter(
                  //         (msg) => !msg.isRead && msg.senderId !== currentUser?.id
                  //       ).length
                  //     })
                    }

                    // Get the last message for preview
                    const lastMessage: Message | null =
                      conversation.messages.length > 0
                        ? (conversation.messages[
                          conversation.messages.length - 1
                        ] as Message)
                        : null

                    // Get display name - show only the other person's name
                    let displayName = ''

                    if (conversation.isGroupChat && conversation.name) {
                      displayName = conversation.name
                    } else if (otherUser) {
                      // If we have the other user object, use their name
                      displayName = `${otherUser.firstname} ${otherUser.lastname}`
                    } else if (
                      conversation.name &&
                      conversation.name.includes('-')
                    ) {
                      // If name is in format "User1 - User2", extract the other user's name
                      const parts = conversation.name
                        .split('-')
                        .map((p) => p.trim())
                      const currentUserName = `${currentUser.firstname} ${currentUser.lastname}`

                      if (parts[0] === currentUserName) {
                        displayName = parts[1]
                      } else {
                        displayName = parts[0]
                      }
                    } else {
                      // Fallback to the conversation name or "Chat"
                      displayName = conversation.name || 'Chat'
                    }
                    return (
                      <div
                        key={conversation.id}
                        className={cn(
                          'flex items-center space-x-3 p-3 rounded-lg cursor-pointer',
                          isSelected
                            ? 'bg-blue-50 hover:bg-blue-100'
                            : 'hover:bg-gray-100',
                          hasUnread && 'font-semibold'
                        )}
                        onClick={() => onSelectConversation(conversation)}
                      >
                        <Avatar>
                          <AvatarImage
                            src={
                              conversation.isGroupChat
                                ? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=128&background=random`
                                : otherUser
                                  ? chatUtils.getAvatarUrl(otherUser)
                                  : displayName && displayName !== 'Chat'
                                    ? `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&size=128&background=random`
                                    : ''
                            }
                          />
                          <AvatarFallback>
                            {conversation.isGroupChat ? (
                              <Users className="h-5 w-5" />
                            ) : otherUser ? (
                              chatUtils.getInitials(otherUser)
                            ) : (
                              // If no otherUser found, try to get initials from the display name
                              displayName && displayName !== 'Chat' ?
                                displayName.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2) :
                                'GC'
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-center">
                            <p
                              className={cn(
                                'truncate',
                                hasUnread && 'font-semibold'
                              )}
                            >
                              {displayName}
                            </p>
                            {lastMessage && (
                              <p className="text-xs text-gray-500">
                                {format(
                                  new Date(lastMessage.createdAt),
                                  'HH:mm'
                                )}
                              </p>
                            )}
                          </div>
                          {otherUser && (otherUser.role || (otherUser as any).userRole) && (
                            <Badge variant="outline" className="text-xs">
                              {otherUser.role || (otherUser as any).userRole}
                            </Badge>
                          )}
                          {lastMessage && (
                            <p
                              className={cn(
                                'text-sm text-gray-500 truncate',
                                hasUnread && 'text-gray-900 font-semibold'
                              )}
                            >
                              {lastMessage.senderId === currentUser.id
                                ? t('routes.chat.sidebar.conversations.you')
                                : ''}{' '}
                              {/* Check if this is a file message */}
                              {lastMessage.messageType === 'file' || lastMessage.messageType === 'image' ? (
                                (() => {
                                  // Get file extension from fileName or mimeType
                                  let extension = '';
                                  if (lastMessage.fileName) {
                                    const parts = lastMessage.fileName.split('.');
                                    extension = parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
                                  } else if (lastMessage.mimeType) {
                                    // Extract extension from mimeType (e.g., "image/png" -> "png")
                                    const mimeParts = lastMessage.mimeType.split('/');
                                    if (mimeParts.length > 1) {
                                      extension = mimeParts[1].split(';')[0].toLowerCase();
                                    }
                                  }
                                  return `file.${extension || 'file'}`;
                                })()
                              ) : (
                                lastMessage.content
                              )}
                            </p>
                          )}
                        </div>
                        {hasUnread && (
                          <div className="flex flex-col items-center">
                            <div className="w-2 h-2 bg-blue-500 rounded-full mb-1"></div>
                            {unreadCount > 1 && (
                              <span className="text-xs bg-blue-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                                {unreadCount}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })
                ) : (
                  <p className="text-center text-gray-500 p-4">
                    {t('routes.chat.sidebar.conversations.empty')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Chat area - adjusted for better spacing */}
        <div className="flex-1 flex flex-col">
          {selectedConversation ? (
            selectedConversation.isGroupChat && currentUser ? (
              // Group Chat View
              <GroupChatView
                conversation={selectedConversation}
                currentUserId={currentUser.id}
              >
                {/* Messages container */}
                <div
                  className="flex-1 overflow-y-auto p-3 bg-gray-50 relative"
                  ref={messagesContainerRef}
                >
                  {selectedConversation.messages.map((msg: Message) => {
                    const isCurrentUser = msg.senderId === currentUser.id
                    return (
                      <div
                        key={msg.id}
                        ref={(el) => setMessageRef(msg.id, el)}
                        data-message-id={msg.id}
                        className={cn(
                          'mb-3 flex',
                          isCurrentUser ? 'justify-end' : 'justify-start'
                        )}
                      >
                        <div
                          className={cn(
                            'max-w-[70%] rounded-lg p-2',
                            isCurrentUser
                              ? 'bg-blue-500 text-white'
                              : 'bg-white text-gray-900'
                          )}
                        >
                          {/* Show sender name for group chats */}
                          {!isCurrentUser && (
                            <p className="text-xs font-semibold mb-1 opacity-70">
                              {msg.sender?.firstname} {msg.sender?.lastname}
                            </p>
                          )}
                          {msg.messageType === 'file' || msg.messageType === 'image' ? (
                            // Ensure all required file properties are present
                            msg.fileName && msg.filePath && msg.fileSize && msg.mimeType ? (
                              <ChatFileMessage 
                                message={{
                                  id: msg.id,
                                  content: msg.content,
                                  fileName: msg.fileName,
                                  filePath: msg.filePath,
                                  fileSize: msg.fileSize,
                                  mimeType: msg.mimeType,
                                  messageType: msg.messageType as 'file' | 'image',
                                  senderId: msg.senderId,
                                  createdAt: msg.createdAt,
                                  isRead: msg.isRead,
                                  temporary: msg.temporary,
                                  error: msg.error
                                }}
                                isCurrentUser={isCurrentUser}
                              />
                            ) : (
                              <p className="text-sm text-gray-500 italic">File message loading...</p>
                            )
                          ) : (
                            <p className="text-sm break-words whitespace-pre-wrap">
                              {msg.content}
                            </p>
                          )}
                          <p
                            className={cn(
                              'text-xs mt-1',
                              isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                            )}
                          >
                            {format(new Date(msg.createdAt), 'HH:mm')}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message input */}
                <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200">
                  {/* File upload component */}
                  <div className="p-2 border-b border-gray-100">
                    <ChatFileUpload
                      ref={fileUploadRef}
                      conversationId={selectedConversationId || ''}
                      onFileMessageSent={handleSendFileMessage}
                      onError={() => {
                        // console.error('File upload error:', error)
                      }}
                      onFileSelected={handleFileSelected}
                      onMessageChange={setMessage}
                      message={message}
                      hideActionButtons={true}
                    />
                  </div>

                  {/* Text message input */}
                  <div className="p-2">
                    <form
                      onSubmit={handleSendMessageWithFile}
                      className="flex items-center space-x-2"
                    >
                      <button
                        type="button"
                        onClick={() => fileUploadRef.current?.selectFile?.()}
                        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded-md"
                        disabled={!!selectedFile}
                      >
                        <Paperclip size={16} />
                      </button>
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('routes.chat.chatArea.messageInput')}
                        className="flex-1 p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoComplete="off"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSendMessageWithFile(e)
                          }
                        }}
                      />
                      <button
                        type="submit"
                        className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!message.trim() && !selectedFile}
                      >
                        <Send size={16} />
                      </button>
                    </form>
                  </div>
                </div>
              </GroupChatView>
            ) : (
              // Private Chat View (existing UI)
              <>
                {/* Chat header - more compact */}
                <div className="p-2 border-b border-gray-200 bg-white">
                  <div className="flex items-center space-x-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage
                        src={
                          getOtherParticipant(selectedConversation)
                            ? chatUtils.getAvatarUrl(
                              getOtherParticipant(selectedConversation)!
                            )
                            : ''
                        }
                      />
                      <AvatarFallback>
                        {getOtherParticipant(selectedConversation)
                          ? chatUtils.getInitials(
                            getOtherParticipant(selectedConversation)!
                          )
                          : 'GC'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium text-sm">
                        {getOtherParticipant(selectedConversation)
                          ? `${getOtherParticipant(selectedConversation)!.firstname
                          } ${getOtherParticipant(selectedConversation)!.lastname
                          }`
                          : selectedConversation.name || 'Group Chat'}
                      </p>
                      {getOtherParticipant(selectedConversation) && (
                        <p className="text-xs text-gray-500">
                          {getOtherParticipant(selectedConversation)!.role}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages container - adjusted height */}
                <div
                  className="flex-1 overflow-y-auto p-3 bg-gray-50 relative"
                  ref={messagesContainerRef}
                  style={{
                    height: 'calc(100vh - 180px)', // Adjusted for back button
                    overflowY: 'auto',
                    scrollBehavior: 'auto',
                    scrollbarWidth: 'thin',
                    overscrollBehavior: 'contain',
                  }}
                >
                  <div className="space-y-3 min-h-full flex flex-col justify-end">
                    {selectedConversation.messages.map((msg: Message) => {
                      const isCurrentUser = msg.senderId === currentUser.id

                      // Check if this is a file message
                      if (msg.messageType === 'file' || msg.messageType === 'image') {
                        // Ensure all required file properties are present
                        if (msg.fileName && msg.filePath && msg.fileSize && msg.mimeType) {
                          return (
                            <div
                              key={msg.id}
                              className={cn(
                                'flex',
                                isCurrentUser ? 'justify-end' : 'justify-start'
                              )}
                              ref={(el) => setMessageRef(msg.id, el)}
                              data-message-id={msg.id}
                            >
                              <ChatFileMessage
                                message={{
                                  id: msg.id,
                                  content: msg.content,
                                  fileName: msg.fileName,
                                  filePath: msg.filePath,
                                  fileSize: msg.fileSize,
                                  mimeType: msg.mimeType,
                                  messageType: msg.messageType as 'file' | 'image',
                                  senderId: msg.senderId,
                                  createdAt: msg.createdAt,
                                  isRead: msg.isRead,
                                  temporary: msg.temporary,
                                  error: msg.error
                                }}
                                isCurrentUser={isCurrentUser}
                              />
                            </div>
                          )
                        }
                      }

                      // Regular text message
                      return (
                        <div
                          key={msg.id}
                          className={cn(
                            'flex',
                            isCurrentUser ? 'justify-end' : 'justify-start'
                          )}
                          ref={(el) => setMessageRef(msg.id, el)}
                          data-message-id={msg.id}
                        >
                          <div
                            className={cn(
                              'max-w-[70%] rounded-lg p-2',
                              isCurrentUser
                                ? 'bg-blue-500 text-white'
                                : 'bg-white border border-gray-200'
                            )}
                          >
                            <p className="text-sm">{msg.content}</p>
                            <div
                              className={cn(
                                'text-xs mt-1 flex justify-between items-center',
                                isCurrentUser ? 'text-blue-100' : 'text-gray-400'
                              )}
                            >
                              <span>
                                {format(new Date(msg.createdAt), 'HH:mm')}
                              </span>
                              {msg.temporary && (
                                <span className="ml-2">
                                  {t('routes.chat.chatArea.sending')}
                                </span>
                              )}
                              {msg.error && (
                                <span className="ml-2 text-red-500">
                                  {t('routes.chat.chatArea.error')}
                                </span>
                              )}
                              {!msg.temporary &&
                                !msg.error &&
                                isCurrentUser &&
                                msg.isRead && (
                                  <span className="ml-2 text-blue-100" style={{ letterSpacing: '1px' }}>
                                    ✓
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Message input - more compact and fixed at bottom */}
                <div className="sticky bottom-0 z-10 bg-white border-t border-gray-200">
                  {/* File upload component */}
                  <div className="p-2 border-b border-gray-100">
                    <ChatFileUpload
                      ref={fileUploadRef}
                      conversationId={selectedConversationId || ''}
                      onFileMessageSent={handleSendFileMessage}
                      onError={() => {
                        // console.error('File upload error:', error)
                        // You can add toast notification here if needed
                      }}
                      onFileSelected={handleFileSelected}
                      onMessageChange={setMessage}
                      message={message}
                      hideActionButtons={true}
                    />
                  </div>

                  {/* Text message input */}
                  <div className="p-2">
                    <form
                      onSubmit={handleSendMessageWithFile}
                      className="flex items-center space-x-2"
                    >
                      <button
                        type="button"
                        onClick={() => fileUploadRef.current?.selectFile?.()}
                        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded-md"
                        disabled={!!selectedFile}
                      >
                        <Paperclip size={16} />
                      </button>
                      <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t('routes.chat.chatArea.messageInput')}
                        className="flex-1 p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        autoComplete="off"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault()
                            handleSendMessageWithFile(e)
                          }
                        }}
                      />
                      <button
                        type="submit"
                        className="p-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={!message.trim() && !selectedFile}
                      >
                        <Send size={16} />
                      </button>
                    </form>
                  </div>
                </div>
              </>
            )
          ) : (
            <div className="flex-1 flex items-center justify-center bg-gray-50">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">
                  {t('routes.chat.chatArea.empty.title')}
                </h3>
                <p className="text-gray-500 mt-1">
                  {t('routes.chat.chatArea.empty.description')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Group Dialog */}
      <CreateGroupDialog
        open={isCreateGroupOpen}
        onOpenChange={setIsCreateGroupOpen}
        onGroupCreated={(conversationId) => {
          // Select the newly created group
          handleSelectConversation({ id: conversationId } as any)
        }}
      />
    </div>
  )
}
